import { ReplyIcon } from "@heroicons/react/solid";
import { Collapse, useTheme } from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import * as Sentry from "@sentry/nextjs";
import firebase from "firebase/app";
import { useTranslation } from "next-i18next";
import React, { useMemo, useState } from "react";

import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { CommentAvatarSize } from "@/consts/avatarSizes";
import { CommentPlaceholder } from "@/features/comment/components/CommentPlaceholder";
import { updateAfterComment } from "@/features/comment/helper";
import { ParentCommentProps } from "@/features/comment/type";
import { ErrorStateText } from "@/features/common/components/informationContainer";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import {
  CommentStatusEnum,
  useCommentPageQuery,
} from "@/generated/generated-hooks";

import {
  CommentActionButton,
  CommentAvatar,
  CommentContent,
  CommentName,
  CommentTime,
} from ".";
import { CommentTextarea } from "../CommentTextarea/CommentTextarea";
import { ChildComment } from "./ChildComment";
import { CommentRemoveButton } from "./CommentRemoveButton";

export const ParentComment: React.FC<ParentCommentProps> = (props) => {
  const { comment, postId, isPostOwner, canLeaveComment } = props;
  const [replying, setReplying] = useState(false);
  const { user: me } = useUserContext();
  const { user, content, numberOfReplies, createdAt, updatedAt, _id } = comment;
  const { avatar, displayName, accountName } = user;
  const commentWriter = user._id === me?._id;
  const theme = useTheme();
  const { t } = useTranslation(["post", "common"]);
  const [showComments, setShowComments] = useState(true);

  const onClickReply = () => {
    firebase
      .analytics()
      .logEvent(AnalyticsEventEnum.CommentToggleReplyParentInputField, {
        postId,
        parentCommentId: _id,
        setOpen: !replying,
      });
    setReplying(!replying);
  };

  const onCommentMutationUpdated = () => {
    setReplying(false);
    setShowComments(true);
  };

  const onClickShowChildren = () => {
    firebase
      .analytics()
      .logEvent(AnalyticsEventEnum.CommentToggleShowChildren, {
        postId,
        parentCommentId: _id,
        setOpen: !showComments,
      });
    setShowComments(!showComments);
  };

  return (
    <div className="flex w-full py-4 space-x-3.5">
      {/* Left Column */}
      {/* Avatar */}
      <CommentAvatar
        avatarUrl={avatar?.url}
        displayName={displayName}
        accountName={accountName}
      />

      {/* Right Column */}
      <div className="flex flex-col flex-1 space-y-1 min-w-0">
        {/* Name, Time, RepliesCount*/}
        <div className="flex space-x-2 items-center min-w-0">
          <CommentName displayName={displayName} accountName={accountName} />

          <CommentTime time={new Date(updatedAt)} />

          {/* Number of Replies */}
          {numberOfReplies > 0 && (
            <span
              className="flex items-center touchable text-sm text-gray-500 whitespace-nowrap"
              onClick={onClickShowChildren}
            >
              {numberOfReplies > 1
                ? t("post:replies", { count: numberOfReplies })
                : t("post:reply", { count: numberOfReplies })}
              {!showComments ? (
                <ArrowDropDownIcon
                  style={{ color: theme.palette.text.secondary }}
                />
              ) : (
                <ArrowDropUpIcon
                  style={{ color: theme.palette.text.secondary }}
                />
              )}
            </span>
          )}
        </div>

        {/* Content */}
        <CommentContent content={content} />

        {/* Collapsed comments */}
        <Collapse in={showComments} timeout="auto" mountOnEnter unmountOnExit>
          <CommentList
            postId={postId}
            parentCommentId={_id}
            isPostOwner={isPostOwner}
          />
        </Collapse>

        <div className="flex items-center space-x-4 ">
          {/* Reply */}
          <CommentActionButton
            onClick={onClickReply}
            icon={ReplyIcon}
            label={t("post:comment.replyComment")}
          />

          {/* Remove Comment Button */}
          <CommentRemoveButton
            commentId={_id}
            isPostOwner={isPostOwner || commentWriter}
          />
        </div>

        {replying && (
          <CommentTextarea
            postId={postId}
            parentCommentId={_id}
            parentName={displayName}
            update={updateAfterComment}
            onUpdated={onCommentMutationUpdated}
            canLeaveComment={canLeaveComment}
          />
        )}
      </div>
    </div>
  );
};

const CommentList = ({
  postId,
  parentCommentId,
  isPostOwner,
}: {
  postId: string;
  parentCommentId: string;
  isPostOwner: boolean;
}) => {
  const { t } = useTranslation("post");
  const { data, error, loading, fetchMore, variables } = useCommentPageQuery({
    ssr: false,
    skip: !postId || !parentCommentId,
    variables: {
      input: { postId, parentCommentId },
      avatarSize: {
        maxWidth: CommentAvatarSize.MaxWidth,
        maxHeight: CommentAvatarSize.MaxHeight,
      },
    },
    notifyOnNetworkStatusChange: true,
  });

  const onFetchMore = async () => {
    firebase.analytics().logEvent(AnalyticsEventEnum.CommentShowMoreChildren, {
      postId,
      parentCommentId,
    });
    if (!data) return;
    const { hasNext, next } = data.commentPage.paging;
    if (loading || !hasNext || !next) return;

    fetchMore({
      variables: { ...variables, paging: { ...variables.paging, next } },
    }).catch((e) => Sentry.captureException(e));
  };

  const uniqueData = useMemo(() => {
    if (!data?.commentPage?.items) return [];
    return data?.commentPage?.items.filter(
      (item, index, arr) =>
        index === arr.findIndex((comment) => comment._id === item._id) &&
        item.commentStatus === CommentStatusEnum.Active,
    );
  }, [data]);

  return (
    <div>
      <div>
        {uniqueData.map((item) => {
          return (
            <div className="flex" key={item._id}>
              <ChildComment comment={item} isPostOwner={isPostOwner} />
            </div>
          );
        })}
        {!data && loading && <CommentPlaceholder count={1} />}
      </div>
      {data?.commentPage?.paging?.hasNext && !loading && (
        <span
          className="touchable px-4 text-sm text-gray-500"
          onClick={onFetchMore}
        >
          {t("post:showMore")}
        </span>
      )}
      {error && <ErrorStateText message={error.message ?? null} />}
    </div>
  );
};
